import { ApolloProvider } from "@apollo/client";
import { GlobalStyles, useMediaQuery } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import App from "next/app";
import Head from "next/head";
import { useRouter } from "next/router";
import React, { useEffect, useRef, useState } from "react";
import parser from "ua-parser-js";

import ErrorBoundary from "components/errors/ErrorBoundary";
import Layout from "components/shared/AppLayout";
import ProtectedPage from "components/shared/ProtectedPage";
import Snackbar from "components/shared/Snackbar";
import useIdentity from "hooks/useIdentity";
import useSite from "hooks/useSite";
import createClient from "lib/apollo/createClient";
import { connectAsUser, initializeClient } from "lib/chat-state";
import { AppProvider, useApp } from "lib/common/appProvider";
import { shouldHideChat } from "lib/concierge/all-chat";
import {
  hideZendeskChat,
  initializeZendeskChat,
  showZendeskChat,
} from "lib/concierge/zendesk-chat";
import trackPage from "lib/segment/trackPage";
import baseUrl from "lib/site/baseUrl";
import getSiteConfig from "lib/site/getSiteConfig";
import theme from "styles/theme";
import "styles/global.css";

const Wrapper = ({ children }) => children;

function PageLayout({ Component, pageProps, site }) {
  const {
    layoutProps = {},
    isPublic = false,
    Layout: ComponentLayout = Layout,
  } = Component;
  const Protected = isPublic ? Wrapper : ProtectedPage;
  const { user } = useApp("auth");
  const [zendeskChatInitialized, setZendeskChatInitialized] = useState(false);
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const streamChatToken = user?.externalTokens?.getStreamChat;

  const { asPath } = useRouter();
  const { stream, loginUrl } = site;
  const { brandCode, communityCode, userId } = useIdentity();
  const { apiKey: streamApiKey } = stream;

  function getLoginUrl(redirect) {
    return `${loginUrl}?redirect=${encodeURIComponent(redirect)}`;
  }

  useEffect(() => {
    const hideChat = shouldHideChat({ isXs, path: asPath });

    if (site && site.zendesk) {
      initializeZendeskChat({
        apiKey: site.zendesk.apiKey,
        hide: hideChat,
      });
      setZendeskChatInitialized(true);
    }
  }, [site, asPath, isXs]);

  useEffect(() => {
    if (userId) {
      if (!streamApiKey) {
        throw new Error("No Stream API key configured");
      }
      if (streamChatToken) {
        initializeClient(streamApiKey); // chat client
        connectAsUser({ userId, token: streamChatToken, communityCode });
      }
    }
  }, [userId, streamApiKey, streamChatToken, communityCode]);

  // Hide Concierge Chat on route change for some routes
  useEffect(() => {
    const hideChat = shouldHideChat({ isXs, path: asPath });
    if (zendeskChatInitialized) {
      if (hideChat) {
        hideZendeskChat();
      } else {
        showZendeskChat();
      }
    }
  }, [brandCode, zendeskChatInitialized, asPath, isXs]);

  return (
    <Protected loginUrl={!isPublic && getLoginUrl(baseUrl(asPath))}>
      <ComponentLayout {...layoutProps}>
        <Component {...pageProps} />
      </ComponentLayout>
    </Protected>
  );
}

// if you were using Segment
// function renderSnippet() {
//   // eslint-disable-next-line react-hooks/rules-of-hooks
//   const { segmentWriteKey } = useApp("site");

//   const opts = {
//     apiKey: segmentWriteKey,
//     // Note: the page option only covers SSR tracking.
//     // MyApp useEffect() is used to track other events using `window.analytics.page()`
//     page: false,
//   };

//   return process.env.NODE_ENV === "development"
//     ? snippet.max(opts)
//     : snippet.min(opts);
// }

function AppHead({ site }) {
  const { name } = site;
  return (
    <Head>
      <title>{name}</title>
      <meta
        content="minimum-scale=1, initial-scale=1, maximum-scale=1, width=device-width, user-scalable=0"
        name="viewport"
      />
      {/* <script
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: renderSnippet() }}
        key="segment"
      /> */}
    </Head>
  );
}

export default function MyApp({
  Component,
  pageProps = {},
  env,
  getSite,
  isMobile,
}) {
  const router = useRouter();
  const snackbarRef = useRef(null);
  const { config: site } = useSite(getSite);
  function handleRouteChange() {
    trackPage();
  }

  useEffect(() => {
    // Remove the server-side injected CSS.
    const jssStyles = document.querySelector("#jss-server-side");
    if (jssStyles) {
      jssStyles.parentElement.removeChild(jssStyles);
    }
    // listen to router changes to register with Segment
    router.events.on("routeChangeComplete", handleRouteChange);

    // --vh variable is for use in place of the vh css length unit due to it's limitations on mobile browsers
    const setVH = () => {
      document
        .querySelector(":root")
        .style.setProperty("--vh", `${window.innerHeight}px`);
    };

    setVH();

    window.addEventListener("resize", setVH);

    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
      window.removeEventListener("resize", setVH);
    };
  });

  useEffect(() => {
    // fire initial page load
    handleRouteChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ApolloProvider client={createClient(site.graphEndpoint)}>
      <AppProvider
        env={env}
        getSite={getSite}
        isMobile={isMobile}
        snackbarRef={snackbarRef}
      >
        <AppHead site={site} />
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <GlobalStyles
              styles={{
                a: {
                  fontweight: 600,
                  textDecoration: "none",
                  color: "#0b79d0",
                  wordBreak: "break-word",
                },
              }}
            />
            <CssBaseline />
            <ErrorBoundary>
              <Snackbar reference={snackbarRef} />
              <PageLayout
                Component={Component}
                pageProps={pageProps}
                site={site}
              />
            </ErrorBoundary>
          </ThemeProvider>
        </StyledEngineProvider>
      </AppProvider>
    </ApolloProvider>
  );
}

MyApp.getInitialProps = async (app) => {
  const {
    ctx: { res, req },
  } = app;

  const initialProps = await App.getInitialProps(app);

  const ua = parser(req?.headers["user-agent"]);
  const isMobile =
    ua?.device?.type === "mobile" || ua?.device?.type === "tablet";

  let siteConfig;

  const getSite = () => siteConfig;

  if (req) {
    siteConfig = await getSiteConfig();
    if (!siteConfig) {
      res.writeHead(404);
      res.end();
    }
    res.setHeader(
      "Cache-Control",
      "no-cache, no-store, max-age=0, must-revalidate"
    );
  }

  return { ...initialProps, env: process.env.CCO_ENV, getSite, isMobile };
};
